import React, { useEffect, useMemo, useState } from "react";
import { useData } from "../lib/store.jsx";
import { Panel, BenchBars, Bars } from "../components/ui.jsx";
import { exportXlsx } from "../lib/forecastXlsx.js";
import { REGISTERED_BEDS as BEDS, BENCH, gbp0, gbpCompact } from "../lib/data.js";

/* Budget explorer. Takes one full financial year of the management accounts
   (Oct → Sep) as the base and lets every line move. Nothing is saved to the
   server: the scenario lives in this browser only. */

// Sheet structure: these lines sit between "Less Cost of Sales" and
// "Total Cost of Sales"; everything else in pl_line_items is overheads.
const COST_OF_SALES = ["Direct Wages", "Employer's NI on direct wages",
  "Pension Scheme", "Agency Cost", "Food purchases"];
const WAGE_GROUP = ["Direct Wages", "Employer's NI on direct wages",
  "Pension Scheme", "Agency Cost"];
// On-costs follow the wage bill automatically, on top of their own slider.
const FOLLOW_WAGES = ["Employer's NI on direct wages", "Pension Scheme"];
// Below EBITDA in every sheet — never part of the forecast.
const BELOW_EBITDA = ["Interest Paid", "Corporation Tax"];
// Added back for Adjusted EBITDA (the sheet's "* Exceptional" list).
const EXCEPTIONAL = ["Consulting", "Legal Expenses", "Motor Vehicle Expenses",
  "Office Rent", "Private Pension", "Private Medical"];
// Default "moves with occupancy" lines: spend per resident.
const VARIABLE = ["Food purchases", "Consumables"];
// Interest and corporation tax: one line, last year's EBITDA less its
// profit after tax (the database doesn't hold them separately).
export const BELOW = "Interest & corporation tax";

const WEEKS = 365 / 7;
const KEY = "aria-forecast-v1";
export const blank = { occ: null, feeWk: null, fee: 0, infl: 0, lines: {}, vary: {} };

const fyOf = (month) => {
  const [y, m] = month.split("-").map(Number);
  return m >= 10 ? y : y - 1;
};
export const fyName = (y) => `${y}-${String(y + 1).slice(-2)}`;
const signed = (v, dp = 0) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(dp)}%`;
const money = (v) => (v < 0 ? "−" : "") + gbp0(Math.abs(v));
// Short axis labels so "£550K" isn't clipped at the chart edge.
const axisFmt = (v) => (Math.abs(v) >= 1e6 ? compact(v)
  : (v < 0 ? "−" : "") + "£" + Math.round(Math.abs(v) / 1e3) + "K");
const compact = (v) => (v < 0 ? "−" : "") + gbpCompact(Math.abs(v));

/* Number box that keeps what's being typed ("-", "", "7.") as text and only
   hands a number up when it parses, so "-10" can't turn into "010". */
function NumBox({ value, onChange, ...rest }) {
  const [txt, setTxt] = useState(String(value));
  useEffect(() => {
    if (parseFloat(txt) !== value && !(txt.trim() === "" && value === 0)
      && !(txt === "-" && value === 0)) setTxt(String(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <input type="number" {...rest} value={txt}
      onChange={(e) => {
        setTxt(e.target.value);
        const v = parseFloat(e.target.value);
        onChange(isFinite(v) ? v : 0);
      }}
      onBlur={() => setTxt(String(value))} />
  );
}

/* Every complete Oct–Sep year whose line items add back up to the sheet's
   own EBITDA. A year that doesn't tie is left out rather than shown wrong. */
function baseYears(d) {
  const by = {};
  for (const f of d.fin) {
    const y = fyOf(f.month);
    (by[y] ||= { months: 0, rev: 0, ebitda: 0, np: 0, hasE: true, lines: {} });
    by[y].months += 1;
    by[y].rev += +f.mrr || 0;
    if (f.ebitda == null || f.net_profit == null) by[y].hasE = false;
    by[y].ebitda += +f.ebitda || 0;
    by[y].np += +f.net_profit || 0;
  }
  for (const p of d.pl) {
    const y = fyOf(p.month);
    if (!by[y] || BELOW_EBITDA.includes(p.category)) continue;
    by[y].lines[p.category] = (by[y].lines[p.category] || 0) + (+p.amount || 0);
  }
  const occ = {};
  for (const r of d.daily) {
    const y = fyOf(r.date.slice(0, 7));
    (occ[y] ||= [0, 0]);
    occ[y][0] += +r.beds_occupied || 0;
    occ[y][1] += 1;
  }
  return Object.entries(by)
    .map(([y, b]) => {
      const cost = Object.values(b.lines).reduce((a, v) => a + v, 0);
      return { y: +y, ...b, below: b.ebitda - b.np, occ: occ[y] ? occ[y][0] / occ[y][1] : null,
        ties: b.hasE && Math.abs(b.rev - cost - b.ebitda) < 10 };
    })
    .filter((b) => b.months === 12 && b.ties && b.occ)
    .sort((a, b) => b.y - a.y);
}

/* The whole model: base year + scenario → budget. */
export function run(base, s) {
  const vol = s.occ / base.occ;
  // Starting weekly fee: last year's average unless one is typed in.
  const wk0 = base.rev / base.occ / WEEKS;
  const wk = s.feeWk || wk0;
  const revenue = base.rev * vol * (wk / wk0) * (1 + s.fee / 100);
  const wagePct = s.lines["Direct Wages"] || 0;
  const lines = {};
  for (const [cat, amt] of Object.entries(base.lines)) {
    const grp = COST_OF_SALES.includes(cat)
      ? (WAGE_GROUP.includes(cat) ? "wages" : "cos") : "opex";
    const v = isVariable(s, cat) ? vol : 1;
    const pct = s.lines[cat] || 0;
    if (amt === 0) { lines[cat] = (s.lines[cat + "|bed"] || 0) * BEDS * v; continue; }
    const follow = FOLLOW_WAGES.includes(cat) ? 1 + wagePct / 100 : 1;
    const infl = grp === "wages" ? 1 : 1 + s.infl / 100;
    lines[cat] = amt * v * (1 + pct / 100) * follow * infl;
  }
  const sum = (f) => Object.entries(lines).filter(([c]) => f(c))
    .reduce((a, [, v]) => a + v, 0);
  const cos = sum((c) => COST_OF_SALES.includes(c));
  const opex = sum((c) => !COST_OF_SALES.includes(c));
  const gp = revenue - cos;
  const ebitda = gp - opex;
  const adj = ebitda + sum((c) => EXCEPTIONAL.includes(c));
  const below = base.below * (1 + (s.lines[BELOW] || 0) / 100);
  return { revenue, lines, cos, opex, gp, ebitda, adj, below, post: ebitda - below,
    wages: sum((c) => WAGE_GROUP.includes(c)) };
}

export const isVariable = (s, cat) => s.vary[cat] ?? VARIABLE.includes(cat);
export { COST_OF_SALES, EXCEPTIONAL, BEDS };

const load = () => {
  try { return JSON.parse(localStorage.getItem(KEY)) || {}; }
  catch { return {}; }
};

function Result({ label, value, base, extra }) {
  const ch = base ? ((value - base) / Math.abs(base)) * 100 : 0;
  const kind = ch > 0.05 ? "up" : ch < -0.05 ? "down" : "flat";
  return (
    <div className="card fc-res s3">
      <div className="label">{label}</div>
      <div className="fc-val">{compact(value)}</div>
      <div className="fc-meta">
        <span className={`badge ${kind}`}>{signed(ch, 1)}</span>
        <span>was {compact(base)}</span>
      </div>
      <div className="fc-sub">{gbp0(value / BEDS)} per bed{extra ? ` · ${extra}` : ""}</div>
    </div>
  );
}

function Slider({ id, label, value, min, max, step, onChange, onReset, show, note }) {
  return (
    <div className="fc-ctl">
      <div className="fc-ctl-top">
        <label htmlFor={id}>{label}</label>
        <b>{show}</b>
      </div>
      <input id={id} type="range" min={min} max={max} step={step} value={value}
        aria-valuetext={show}
        onChange={(e) => onChange(+e.target.value)}
        onDoubleClick={onReset || (() => onChange(0))} />
      {note && <div className="fc-note">{note}</div>}
    </div>
  );
}

function LineRow({ cat, baseAmt, s, set, outAmt, wagePct, fixedOnly }) {
  const zero = baseAmt === 0;
  const k = zero ? cat + "|bed" : cat;
  const val = s.lines[k] || 0;
  const varies = isVariable(s, cat);
  const follow = FOLLOW_WAGES.includes(cat) && wagePct !== 0;
  const id = "fc-" + cat.replace(/\W+/g, "-");
  const setVal = (v) => set((p) => ({ ...p, lines: { ...p.lines, [k]: isFinite(v) ? Math.max(zero ? 0 : -100, v) : 0 } }));
  return (
    <div className={"fc-row" + (val ? " moved" : "")}>
      <div className="fc-name">
        <label htmlFor={id}>{cat}</label>
        <div className="fc-tags">
          {EXCEPTIONAL.includes(cat) && <span className="fc-tag">exceptional</span>}
          {follow && <span className="fc-tag">follows wages</span>}
          {!fixedOnly && <button type="button" className={"fc-tag fc-vary" + (varies ? " on" : "")}
            aria-pressed={varies}
            onClick={() => set((p) => ({ ...p, vary: { ...p.vary, [cat]: !varies } }))}>
            {varies ? "moves with occupancy" : "fixed"}
          </button>}
        </div>
      </div>
      <div className="fc-num fc-base muted">{gbp0(baseAmt)}</div>
      <input id={id} className="fc-range" type="range"
        min={zero ? 0 : -50} max={zero ? 5000 : 50} step={zero ? 50 : 1}
        value={val} onChange={(e) => setVal(+e.target.value)}
        aria-valuetext={zero ? `£${val} per bed` : signed(val)}
        onDoubleClick={() => setVal(0)} />
      <div className="fc-pct">
        <NumBox aria-label={`${cat} ${zero ? "£ per bed" : "% change"}`}
          value={val} step={zero ? 50 : 0.5} onChange={setVal} />
        <span>{zero ? "£/bed" : "%"}</span>
      </div>
      <div className="fc-num fc-out"><b>{gbp0(outAmt)}</b></div>
      <div className="fc-num fc-bed">{gbp0(outAmt / BEDS)}</div>
    </div>
  );
}

export default function Forecast() {
  const { d } = useData();
  const years = useMemo(() => baseYears(d), [d]);
  const saved = useMemo(load, []);
  const [y, setY] = useState(() =>
    years.some((b) => b.y === saved.y) ? saved.y : years[0]?.y);
  const base = years.find((b) => b.y === y);
  const [s, set] = useState(() => ({ ...blank, ...(saved.s || {}) }));
  const [fromSave] = useState(() => !!saved.s && JSON.stringify(saved.s) !== JSON.stringify(blank));
  const sc = { ...s, occ: s.occ ?? (base ? base.occ : 0) };

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify({ y, s })); } catch { /* private mode */ }
  }, [y, s]);

  if (!base) {
    return <Panel title="Forecast" sub="No complete year of management accounts to build from." />;
  }

  const b0 = run(base, { ...blank, occ: base.occ });
  const f = run(base, sc);

  const fee0 = base.rev / base.occ / WEEKS;
  const feeStart = s.feeWk || fee0;
  const cats = Object.keys(base.lines);
  const cosCats = COST_OF_SALES.filter((c) => c in base.lines);
  const opexCats = cats.filter((c) => !COST_OF_SALES.includes(c)).sort();
  const wagePct = s.lines["Direct Wages"] || 0;
  const moved = s.fee || s.feeWk || s.infl || s.occ != null
    || Object.values(s.lines).some(Boolean) || Object.keys(s.vary).length;
  const margin = (v) => `${((v / f.revenue) * 100).toFixed(1)}% margin`;

  const head = (
    <div className="fc-row fc-head" aria-hidden="true">
      <div>Line</div><div className="fc-num">FY {fyName(y)}</div>
      <div>Change</div><div /><div className="fc-num">Budget</div>
      <div className="fc-num">Per bed</div>
    </div>
  );
  const total = (label, a, b) => (
    <div className="fc-row fc-total">
      <div>{label}</div><div className="fc-num fc-base">{gbp0(a)}</div><div /><div />
      <div className="fc-num fc-out"><b>{gbp0(b)}</b></div>
      <div className="fc-num fc-bed">{gbp0(b / BEDS)}</div>
    </div>
  );
  const rows = (list) => list.map((c) => (
    <LineRow key={c} cat={c} baseAmt={base.lines[c]} s={s} set={set}
      outAmt={f.lines[c]} wagePct={wagePct} />
  ));

  return (
    <>
      <div className="card fc-controls s12">
        <div className="fc-intro">
          <div>
            <h2>Budget forecast</h2>
            <p>Starts from the full-year management accounts. Move a slider and every total updates.
              Double-click any slider to put it back.</p>
            {fromSave && moved && <p className="fc-saved">Showing your saved scenario from last time. Reset all to start again.</p>}
          </div>
          <div className="fc-intro-r">
            <label className="fc-select">
              <span>Base year</span>
              <select value={y} onChange={(e) => setY(+e.target.value)}>
                {years.map((b) => <option key={b.y} value={b.y}>FY {fyName(b.y)} (Oct–Sep)</option>)}
              </select>
            </label>
            <button type="button" className="fc-reset" disabled={!moved}
              onClick={() => set(blank)}>Reset all</button>
            <button type="button" className="fc-reset fc-export"
              onClick={() => exportXlsx({ base, b0, f, sc, y, fee0, feeStart })}>Export to Excel</button>
          </div>
        </div>
        <div className="fc-ctls">
          <Slider id="fc-occ" label="Occupancy" min={18} max={BEDS} step={0.1} value={sc.occ}
            onChange={(v) => set((p) => ({ ...p, occ: v }))}
            onReset={() => set((p) => ({ ...p, occ: null }))}
            show={`${sc.occ.toFixed(1)} of ${BEDS} beds · ${((sc.occ / BEDS) * 100).toFixed(1)}%`}
            note={`Last year averaged ${base.occ.toFixed(1)} residents. Moves revenue and any line marked “moves with occupancy”.`} />
          <div className="fc-ctl">
            <div className="fc-ctl-top">
              <label htmlFor="fc-feewk">Average weekly fee</label>
              <b>{gbp0(feeStart * (1 + s.fee / 100))}/week</b>
            </div>
            <div className="fc-money">
              <span>£</span>
              <input id="fc-feewk" type="number" min={0} step={1}
                placeholder={Math.round(fee0)}
                value={s.feeWk ?? ""}
                onChange={(e) => { const v = parseFloat(e.target.value);
                  set((p) => ({ ...p, feeWk: v > 0 ? v : null })); }} />
              <span>per resident per week</span>
            </div>
            <div className="fc-note">{`Starting point. Last year averaged ${gbp0(fee0)} (revenue ÷ residents); leave blank to use it.`}</div>
          </div>
          <Slider id="fc-fee" label="Fee change" min={-10} max={20} step={0.5} value={s.fee}
            onChange={(v) => set((p) => ({ ...p, fee: v }))}
            show={`${signed(s.fee, 1)} · ${gbp0(feeStart * (1 + s.fee / 100))}/week`}
            note="Applied on top of the average weekly fee." />
          <Slider id="fc-infl" label="General cost inflation" min={0} max={15} step={0.5} value={s.infl}
            onChange={(v) => set((p) => ({ ...p, infl: v }))}
            show={signed(s.infl, 1)}
            note="Applies to every cost except wages and on-costs, on top of each line’s own change." />
        </div>
      </div>

      <Result label="Revenue" value={f.revenue} base={b0.revenue} />
      <Result label="Gross profit" value={f.gp} base={b0.gp} extra={margin(f.gp)} />
      <Result label="EBITDA" value={f.ebitda} base={b0.ebitda} extra={margin(f.ebitda)} />
      <Result label="Post-tax profit" value={f.post} base={b0.post} extra={margin(f.post)} />


      <Panel className="s12" title="Line by line"
        sub="Slide each cost up or down. Lines that were £0 last year take a £ per bed figure instead.">
        <div className="fc-table">
          {head}
          <div className="fc-row fc-total fc-rev">
            <div>Revenue</div><div className="fc-num fc-base">{gbp0(b0.revenue)}</div>
            <div className="fc-span">set by occupancy and fees above</div><div />
            <div className="fc-num fc-out"><b>{gbp0(f.revenue)}</b></div>
            <div className="fc-num fc-bed">{gbp0(f.revenue / BEDS)}</div>
          </div>
          <div className="fc-group">Cost of sales</div>
          {rows(cosCats)}
          {total("Total cost of sales", b0.cos, f.cos)}
          {total("Gross profit", b0.gp, f.gp)}
          <div className="fc-group">Overheads</div>
          {rows(opexCats)}
          {total("Total overheads", b0.opex, f.opex)}
          {total("EBITDA", b0.ebitda, f.ebitda)}
          {total("Adjusted EBITDA", b0.adj, f.adj)}
          <div className="fc-group">Below EBITDA</div>
          <LineRow cat={BELOW} baseAmt={base.below} s={s} set={set}
            outAmt={f.below} wagePct={0} fixedOnly />
          {total("Post-tax profit", b0.post, f.post)}
        </div>
        <div className="fc-foot">
          Ties to the management accounts: FY {fyName(y)} EBITDA {money(base.ebitda)},
          profit after tax {money(base.np)}. Interest and corporation tax are one line here and
          don’t move with profit unless you slide them.
        </div>
      </Panel>

      <Panel className="s6" title="Budget per bed vs sector"
        sub="LaingBuisson / Knight Frank 2024, per bed per year">
        <BenchBars rows={[
          { label: "Revenue / bed", value: f.revenue / BEDS, bench: BENCH.revPerBed, fmt: gbpCompact },
          { label: "Gross profit / bed", value: f.gp / BEDS, bench: BENCH.gpPerBed, fmt: gbpCompact },
          { label: "Adjusted EBITDA / bed", value: f.adj / BEDS, bench: BENCH.ebitdaPerBed, fmt: gbpCompact },
          { label: "Wages / bed", value: f.wages / BEDS, bench: BENCH.wagesPerBed, fmt: gbpCompact },
        ]} />
      </Panel>
      <Panel className="s6" title="Last year vs budget"
        sub="updates as you slide">
        <Bars data={[
          ["Revenue", b0.revenue, f.revenue],
          ["EBITDA", b0.ebitda, f.ebitda],
          ["Adjusted EBITDA", b0.adj, f.adj],
        ].map(([x, a, b]) => ({ x, [`FY ${fyName(y)}`]: Math.round(a), Budget: Math.round(b) }))}
          x="x" bars={[{ key: `FY ${fyName(y)}`, name: `FY ${fyName(y)}` },
            { key: "Budget", name: "Budget" }]}
          fmt={axisFmt} h={300} />
      </Panel>
    </>
  );
}
